import {
  ContactPage,
  DayOfWeek,
  FAQPage,
  Organization,
  Product,
  RealEstateListing,
  Service,
  WebPage,
  WithContext,
} from 'schema-dts';

import { FaqItem } from './faq.utils';
import { SITE_URL } from '../consts';
import { COMMON_JS_SEARCH_QUERY_GENERIC } from '../consts/commonJsIndex';
import { generateUrl } from './office.utils';
import { I18nContextType } from '../../libs/i18n';
import { IFlat, IFlatWithOffice } from '../model/flat.model';
import { IMapArea } from '../model/mapArea.model';
import { IOffice } from '../model/office.model';

import { getBreadcrumbItems as getFlatBreadcrumbItems } from '../../components/shared/flatBreadcrumb';
import { getBreadcrumbItems as getOfficesBreadcrumbItems } from '../../components/offices/officesBreadcrumb';

const logo = `${SITE_URL}/images/LogoHeaderWeb.svg`;

export const generateContactPageStructuredData = (): WithContext<
  ContactPage
> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/contacto`,
    },
    publisher: {
      '@type': 'LocalBusiness',
      '@id': SITE_URL,
      ...getGeneralInfo(),
    },
  };
};

export const generateFlatDetailPageStructuredData = (
  flat: IFlatWithOffice,
  url: string
): WithContext<Product> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: flat.address.substring(0, 150),
    description: flat.description_ES,
    image: flat.pictureUrls,
    sku: flat.id,
    brand: {
      '@type': 'Brand',
      name: 'Inmobiliaria Núcleo',
    },
    offers: {
      '@type': 'Offer',
      price: flat.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url,
      seller: getRealEstateAgent(flat.office),
    },
  };
};

export const generateHomePageStructuredData = (
  query: string
): WithContext<Organization> => {
  if (query !== COMMON_JS_SEARCH_QUERY_GENERIC) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    ...getGeneralInfo(),
  };
};

export const generateOfficeDetailPageStructuredData = (
  office: IOffice,
  i18n: I18nContextType,
  countyCity: [string | undefined, string | undefined],
  slug: string
): WithContext<WebPage> => {
  const breadcrumbItems = getOfficesBreadcrumbItems(
    i18n,
    [office],
    countyCity[0],
    countyCity[1],
    slug
  );

  const realEstateAgent = getRealEstateAgent(office);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        item: item.href ? `${SITE_URL}${item.href}` : undefined,
      })),
    },
    mainEntityOfPage: {
      ...realEstateAgent,
    },
  };
};

export const generateOfficeListPageStructuredData = (
  offices: IOffice[],
  i18n: I18nContextType,
  countyCity: [string | undefined, string | undefined]
): WithContext<WebPage> => {
  const breadcrumbItems = getOfficesBreadcrumbItems(
    i18n,
    offices,
    countyCity[0],
    countyCity[1]
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        item: item.href ? `${SITE_URL}${item.href}` : undefined,
      })),
    },
    mainEntity: {
      '@type': 'ItemList',
      name: 'Oficinas',
      itemListElement: offices.map((office, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: getRealEstateAgent(office),
      })),
    },
  };
};

export const generateSearchPageStructuredData = (
  title: string,
  description: string,
  url: string,
  flats: IFlat[],
  i18n: I18nContextType,
  flatType: string,
  countyCityZone: [string | undefined, string | undefined, string | undefined],
  mapArea?: IMapArea
): WithContext<RealEstateListing> => {
  const breadcrumbItems = getFlatBreadcrumbItems(
    i18n,
    flatType,
    countyCityZone[0],
    countyCityZone[1],
    countyCityZone[2],
    undefined,
    mapArea
  );
  breadcrumbItems[breadcrumbItems.length - 1].href = undefined;

  const result: WithContext<RealEstateListing> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: title,
    description,
    url,
    mainEntityOfPage: url,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        item: item.href ? `${SITE_URL}${item.href}` : undefined,
      })),
    },
    offers: flats.map((flat) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Product',
        name: flat.address.substring(0, 150),
        description: flat.description_ES.slice(0, 100),
        image: flat.pictureUrls[0],
        sku: flat.id,
        brand: 'Inmobiliaria Núcleo',
        offers: {
          '@type': 'Offer',
          price: flat.price,
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
        },
      },
    })),
  };

  if (breadcrumbItems.length > 2) {
    result.spatialCoverage = {
      '@type': 'Place',
      name: `${breadcrumbItems[breadcrumbItems.length - 1].label}, España`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: countyCityZone[1],
        addressRegion: countyCityZone[0],
        addressCountry: 'ES',
      },
    };
  }

  return result;
};

export const generateSellHousePageStructuredData = (): WithContext<Service> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Vender casa',
    url: 'https://www.inmobiliarianucleo.com/vender-casa',
    description:
      'Vende tu vivienda de forma rápida y segura con Inmobiliaria Núcleo. Contamos con expertos en el mercado inmobiliario.',
    provider: {
      '@type': 'Organization',
      ...getGeneralInfo(),
    },
    areaServed: 'ES',
    serviceType: 'Venta de propiedades',
    offers: {
      '@type': 'Offer',
      price: 'Consultar',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: 'https://www.inmobiliarianucleo.com/vender-casa',
    },
  };
};

export const generateFaqStructuredData = (
  faqItems: FaqItem[]
): WithContext<FAQPage> => {
  if (faqItems.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item, i) => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.description,
      },
    })),
  };
};

const getGeneralInfo = () => {
  return {
    url: SITE_URL,
    sameAs: [SITE_URL.replace('www.', '')],
    logo,
    name: 'Inmobiliaria Núcleo',
    description:
      'Compra-Venta de viviendas. Encuentra inmuebles al mejor precio. Visita Inmobiliaria Núcleo y conoce nuestra oferta de casas en venta en España.',
    email: 'admin@inmobiliarianucleo.com',
    telephone: '+34965999554',
    address: {
      '@type': 'PostalAddress' as const,
      streetAddress: 'C/ Pérez Medina nº 23 local',
      addressLocality: 'Alicante',
      addressCountry: 'ES',
      addressRegion: 'Alicante',
      postalCode: '03007',
    },
  };
};

const getRealEstateAgent = (office: IOffice) => {
  if (office == null) {
    return null;
  }

  const url = `${SITE_URL}${generateUrl(office)}`;

  return {
    '@type': 'RealEstateAgent' as const,
    '@id': url,
    name: `Inmobiliaria Núcleo en ${office.name}`,
    url,
    image: logo,
    telephone: office.phone,
    address: {
      '@type': 'PostalAddress' as const,
      streetAddress: office.address,
      addressLocality: office.city,
      addressRegion: office.province,
      postalCode: office.postalCode,
      addressCountry: {
        '@type': 'Country' as const,
        name: 'ES',
      },
    },
    geo: {
      '@type': 'GeoCoordinates' as const,
      latitude: office.lat,
      longitude: office.long,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification' as const,
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
        ] as DayOfWeek[],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification' as const,
        dayOfWeek: ['Saturday'] as DayOfWeek[],
        opens: '10:00',
        closes: '14:00',
      },
    ],
    sameAs: [
      'https://www.facebook.com/inmobiliarianucleo',
      'https://www.instagram.com/inmobiliarianucleo',
    ],
  };
};
